import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { api } from '../api';
import type { ColumnKey, ResponseItem } from '../types';
import { Comments } from './Comments';

const EMOJIS = ['👍', '🚨', '💡', '🔥'] as const;

type Props = {
  columnKey: ColumnKey;
  response: ResponseItem;
  currentUser: string;
  text: string;
};

export function ResponseCard({ columnKey, response, currentUser, text }: Props) {
  const [showComments, setShowComments] = useState(false);
  const [busy, setBusy] = useState(false);
  const [localReactions, setLocalReactions] = useState(response.reactions);
  const [commentCount, setCommentCount] = useState(
    response.commentCounts.find(c => c.column_key === columnKey)?.count ?? 0
  );

  const reactCount = (emoji: string) =>
    localReactions.find(r => r.column_key === columnKey && r.emoji === emoji)?.count ?? 0;

  const toggle = async (emoji: string) => {
    setBusy(true);
    const current = reactCount(emoji);
    setLocalReactions(prev => {
      const others = prev.filter(r => !(r.column_key === columnKey && r.emoji === emoji));
      const next = current > 0 ? current - 1 : current + 1;
      return next > 0
        ? [...others, { column_key: columnKey, emoji, count: next }]
        : others;
    });
    try {
      await api.reactions.toggle(response.id, columnKey, emoji, currentUser);
    } finally { setBusy(false); }
  };

  if (!text.trim()) return null;

  const initials = response.memberName.split(' ').map(x => x[0]).join('').slice(0, 2).toUpperCase();

  return (
    <>
      <motion.article
        layout
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        className="card"
      >
        <div className="flex items-center gap-2 mb-2">
          <div className="h-7 w-7 flex items-center justify-center bg-coast-hairline
                          font-heading text-xs">
            {initials}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-widest text-coast-blue truncate">
              {response.memberName}
            </div>
          </div>
          <div className="text-[10px] text-coast-muted">
            {new Date(response.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
          </div>
        </div>

        <p className="text-[15px] leading-relaxed whitespace-pre-wrap">{text}</p>

        <div className="mt-3 flex items-center gap-1.5 flex-wrap">
          {EMOJIS.map(em => {
            const n = reactCount(em);
            return (
              <button
                key={em}
                onClick={() => toggle(em)}
                disabled={busy}
                className={`chip ${n > 0 ? 'border-coast-blue text-coast-blue' : ''}`}
                aria-label={`React ${em}`}
              >
                <span className="text-base leading-none">{em}</span>
                {n > 0 && <span className="tabular-nums text-xs">{n}</span>}
              </button>
            );
          })}
          <button
            onClick={() => setShowComments(true)}
            className="chip ml-auto"
            aria-expanded={showComments}
          >
            💬 <span className="tabular-nums text-xs">{commentCount}</span>
          </button>
        </div>
      </motion.article>

      <AnimatePresence>
        {showComments && (
          <>
            <motion.div
              className="sheet-backdrop"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setShowComments(false)}
            />
            <motion.aside
              className="sheet"
              initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              role="dialog"
              aria-label="Comments"
            >
              <div className="sheet-handle" />
              <header className="px-4 py-2 border-b border-coast-hairline flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-coast-muted">
                    Comments · {response.memberName}
                  </div>
                  <div className="font-heading text-lg leading-tight line-clamp-1">{text}</div>
                </div>
                <button className="btn btn-ghost" onClick={() => setShowComments(false)}>Done</button>
              </header>
              <div className="flex-1 overflow-y-auto px-4 py-3">
                <Comments
                  responseId={response.id}
                  columnKey={columnKey}
                  memberName={currentUser}
                  onCountChange={setCommentCount}
                />
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
